import { isFixedRateLoanProductFields } from '@src/types/OTC/otcGuards';
import type {
  FixedRateLoanDealProductBuilderRow,
  OTCFixedRateLoanFieldsData,
} from '@src/types/OTC/otcProductBuilderTypes';
import {
  getPriceSourceFixingsByFullSymbolFromMapping,
  USD_PEGGED_NO_CONVERSION_INDEX,
  type FixingIndexMappingMap,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import { DayCountConventionEnum, EMPTY_OBJECT, LoanQuoteTerminationTypeEnum, runValidationV1 } from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { object, string, type InferType } from 'yup-v1-latest';
import { getAllowedLoanPaymentCcyAssets } from '../OTCFieldsDialog/loanPaymentCurrencyForRow';
import { LOAN_PAYMENT_PERIOD_OPTIONS } from '../OTCFieldsDialog/productLayoutTokens';
import type { BaseProductBuilderSliceStrategy } from './BaseProductBuilderSliceStrategy';
import { isDurationWithinLoanOtcMax } from './durationComparisonUtils';
import { positiveNumericString } from './validationUtils';

export const FixedRateLoanProductBuilderSliceStrategy: BaseProductBuilderSliceStrategy<FixedRateLoanDealProductBuilderRow> =
  {
    getDefaultValues: (
      existingOTCFieldsForClear?: FixedRateLoanDealProductBuilderRow['otcFields']
    ): NonNullable<FixedRateLoanDealProductBuilderRow['otcFields']> => {
      const loanDefaults: OTCFixedRateLoanFieldsData = {
        productType: 'fixedRateLoan',
        unitLoanAmount: '1',
        termType: LoanQuoteTerminationTypeEnum.Fixed,
        term: null,
        initialTerm: null,
        paymentPeriod: '1M',
        accrualPeriod: '1M',
        dayCountConvention: DayCountConventionEnum.Thirty360,
      };

      if (isFixedRateLoanProductFields(existingOTCFieldsForClear)) {
        return { ...loanDefaults, ...existingOTCFieldsForClear };
      }

      return loanDefaults;
    },

    buildNoSuffixInstrumentName: (updatedRow: WritableDraft<FixedRateLoanDealProductBuilderRow>): string => {
      if (updatedRow && updatedRow.baseAsset) {
        let instrument = `LOAN-${updatedRow.baseAsset}`;
        if (updatedRow.collateralAsset) {
          instrument += `-${updatedRow.collateralAsset}`;
        }
        const termType = updatedRow.otcFields?.termType ?? LoanQuoteTerminationTypeEnum.Fixed;
        const durationSegment =
          termType === LoanQuoteTerminationTypeEnum.OpenTerm
            ? updatedRow.otcFields?.initialTerm
            : updatedRow.otcFields?.term;
        if (durationSegment) {
          instrument += `-${durationSegment}`;
        }
        instrument += '-FIXED';
        return instrument;
      }
      return '';
    },

    updateGridRowData: (
      state: WritableDraft<FixedRateLoanDealProductBuilderRow>,
      rowData: FixedRateLoanDealProductBuilderRow,
      fixingIndexMapping?: FixingIndexMappingMap
    ): void => {
      state.baseAsset = rowData.baseAsset;
      state.collateralAsset = rowData.collateralAsset;
      state.productTemplate = rowData.productTemplate;
      state.uniqueSuffix = rowData.uniqueSuffix;
    },

    updateOTCFields: (
      state: WritableDraft<FixedRateLoanDealProductBuilderRow>,
      otcFields: FixedRateLoanDealProductBuilderRow['otcFields'],
      fixingIndexMapping?: FixingIndexMappingMap
    ): void => {
      // Shallow-copy non-null payloads so we never persist frozen action references in draft state.
      state.otcFields = otcFields == null ? otcFields : { ...otcFields };
    },

    validateFormState: (
      currentRow: FixedRateLoanDealProductBuilderRow,
      fixingIndexMapping?: FixingIndexMappingMap
    ): ReturnType<typeof runValidationV1> => {
      const schema = fixingIndexMapping ? createLoanProductBuilderSchema(fixingIndexMapping) : loanProductBuilderSchema;
      return runValidationV1(schema, currentRow);
    },

    validateOTCFields: (
      otcFields: FixedRateLoanDealProductBuilderRow['otcFields'],
      fixingIndexMapping?: FixingIndexMappingMap
    ): ReturnType<typeof runValidationV1> => {
      return runValidationV1(loanOtcFieldsSchema, otcFields ?? EMPTY_OBJECT);
    },
  };

/** UI-side validation schema for loan OTC Fields form */
export const loanOtcFieldsSchema = object().shape({
  productType: string()
    .oneOf(['fixedRateLoan'] as const)
    .required('Product type is required'),
  unitLoanAmount: positiveNumericString('Unit loan amount'),
  termType: string()
    .oneOf([LoanQuoteTerminationTypeEnum.Fixed, LoanQuoteTerminationTypeEnum.OpenTerm] as const)
    .required('Term type is required'),
  term: string().when('termType', {
    is: LoanQuoteTerminationTypeEnum.Fixed,
    then: schema =>
      schema
        .required('Term is required when Term Type is Fixed')
        .test('term-max', 'Term must be at most 1 year', value => {
          if (!value) {
            return true;
          }
          return isDurationWithinLoanOtcMax(value);
        }),
    otherwise: schema => schema.nullable().oneOf([null], 'Term must be empty when Term Type is Open'),
  }),
  initialTerm: string().when('termType', {
    is: LoanQuoteTerminationTypeEnum.OpenTerm,
    then: schema =>
      schema
        .nullable()
        .optional()
        .test('initial-term-max', 'Initial term must be at most 1 year', value => {
          if (value == null || value === '') {
            return true;
          }
          return isDurationWithinLoanOtcMax(value);
        }),
    otherwise: schema => schema.nullable().oneOf([null], 'Initial Term must be empty when Term Type is Fixed'),
  }),
  loanPaymentCcy: string().required('Loan Payment Currency is required'),
  paymentPeriod: string()
    .oneOf(
      LOAN_PAYMENT_PERIOD_OPTIONS.map(item => item.value),
      'Invalid Payment period symbol'
    )
    .required('Payment period is required'),
  accrualPeriod: string()
    .oneOf(
      LOAN_PAYMENT_PERIOD_OPTIONS.map(item => item.value),
      'Invalid Accrual period symbol'
    )
    .required('Accrual period is required'),
  dayCountConvention: string()
    .oneOf(Object.values(DayCountConventionEnum), 'Invalid Day Count Convention')
    .required('Day Count Convention is required'),
});

export const createLoanProductBuilderSchema = (fixingIndexMapping?: FixingIndexMappingMap) =>
  object({
    productTemplate: string().required('Product Template is required'),
    product: string()
      .oneOf(['fixedRateLoan'] as const)
      .required('Product is required'),
    baseAsset: string()
      .required('Borrowed Asset is required')
      .test('asset-mismatch', 'Borrowed Asset and Collateral Asset cannot be the same', function (value) {
        const { collateralAsset } = this.parent;
        if (!value || !collateralAsset) {
          return true;
        }
        return value !== collateralAsset;
      }),
    collateralAsset: string()
      .required('Collateral Asset is required')
      .test('asset-mismatch', 'Borrowed Asset and Collateral Asset cannot be the same', function (value) {
        const { baseAsset } = this.parent;
        if (!value || !baseAsset) {
          return true;
        }
        return value !== baseAsset;
      }),
    uniqueSuffix: string().nullable(),
    otcFields: loanOtcFieldsSchema.required('OTC fields are required'),
  })
    .test(
      'fixed-loan-payment-ccy-in-allowed-set',
      'Loan Payment Ccy must be Borrowed Asset or Collateral Asset',
      function (row) {
        if (row.product !== 'fixedRateLoan' || !row.baseAsset || !row.otcFields) {
          return true;
        }
        if (!isFixedRateLoanProductFields(row.otcFields)) {
          return true;
        }
        const allowed = getAllowedLoanPaymentCcyAssets(row.baseAsset, row.collateralAsset);
        const ccy = row.otcFields.loanPaymentCcy;
        return !!ccy && allowed.includes(ccy);
      }
    )
    .test(
      'loan-pair-has-fixing',
      'Borrowed Asset and Collateral Asset pair must have at least one fixing',
      function (row) {
        if (!row.baseAsset || !row.collateralAsset) {
          return true;
        }

        const normalizedCollateralAsset =
          row.collateralAsset === 'USDT' || row.collateralAsset === 'USDC' ? 'USD' : row.collateralAsset;
        const normalizedBorrowedAsset = row.baseAsset === 'USDT' || row.baseAsset === 'USDC' ? 'USD' : row.baseAsset;

        if (fixingIndexMapping && fixingIndexMapping.size > 0) {
          const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
            normalizedCollateralAsset,
            normalizedBorrowedAsset,
            fixingIndexMapping
          );
          const availableFixings = fixings.filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name);
          return availableFixings.length > 0;
        }

        return true;
      }
    );

export const loanProductBuilderSchema = createLoanProductBuilderSchema();

export type ValidatedLoanRowData = InferType<typeof loanProductBuilderSchema>;
